// src/contexts/ConfirmContext.tsx
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import ConfirmModal from '../components/ConfirmModal';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

interface ConfirmContextType {
  showConfirm: (options: ConfirmOptions) => void;
  hideConfirm: () => void;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error('useConfirm must be used within ConfirmProvider');
  return context;
};

export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({
    title: 'Подтверждение',
    message: '',
    onConfirm: () => {},
  });

  const showConfirm = useCallback((opts: ConfirmOptions) => {
    setOptions({
      variant: 'danger',
      confirmText: 'Подтвердить',
      cancelText: 'Отмена',
      title: 'Подтверждение',
      ...opts,
    });
    setIsOpen(true);
  }, []);

  const hideConfirm = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleConfirm = async () => {
    try {
      await options.onConfirm();
    } catch (e) {
      console.error('ConfirmModal onConfirm error:', e);
    }
    hideConfirm();
  };

  const handleCancel = () => {
    if (options.onCancel) options.onCancel();
    hideConfirm();
  };

  return (
    <ConfirmContext.Provider value={{ showConfirm, hideConfirm }}>
      {children}
      <ConfirmModal
        isOpen={isOpen}
        title={options.title || 'Подтверждение'}
        message={options.message}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
        variant={options.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
};

// ★ Promise-based обёртка — позволяет await confirm()
export const usePromiseConfirm = () => {
  const { showConfirm } = useConfirm();

  const confirm = (opts: Omit<ConfirmOptions, 'onConfirm' | 'onCancel'>): Promise<boolean> => {
    return new Promise((resolve) => {
      showConfirm({
        ...opts,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  };

  return { confirm };
};
